import {
  Coupon,
  Coupon_HighTierCoupon,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/coupon.types';
import {GetHeaderResponse} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/coupons/v1/landingpage/service.types';
import React, {useEffect} from 'react';
import {isAppOnlyCoupon as getIsAppOnlyCoupon} from '../../../features/Coupons/isAppOnlyCoupon';
import {CouponLandingPageHeaderBaseMobile} from './CouponLandingPageHeaderBase.mobile';
import {useCLPRedirect} from './hooks/useCLPRedirect';

export interface CouponLandingPageHeaderViewMobileProps {
  ['data-testid']?: string;
  setCoupon: React.Dispatch<React.SetStateAction<Coupon | undefined>>;
}

const getRelativeDiscountOrTiers = (highTierCoupon: Coupon_HighTierCoupon) => {
  if (highTierCoupon.discount?.$case === 'relativeDiscount') {
    return {
      discountValue: highTierCoupon.discount.relativeDiscount,
    };
  } else if (highTierCoupon.discount?.tiers.tiers.length === 1) {
    return {
      discountValue: highTierCoupon.discount.tiers.tiers[0]?.discountValue,
      discountCondition: highTierCoupon.discount.tiers.tiers[0]?.condition,
    };
  } else {
    return {
      tiers: highTierCoupon.discount?.tiers.tiers,
    };
  }
};
export const CouponLandingPageHeaderViewMobile: React.FC<
  CouponLandingPageHeaderViewMobileProps & GetHeaderResponse
> = ({
  coupon,
  'data-testid': dataTestId = 'couponLandingPageHeader',
  setCoupon,
  redirect,
}) => {
  useCLPRedirect(redirect);

  useEffect(() => {
    if (coupon) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect
      setCoupon(coupon);
    }
  }, [coupon]);

  if (!coupon) {
    return null;
  }

  const isAppOnlyCoupon = getIsAppOnlyCoupon(coupon);

  switch (coupon.type?.$case) {
    case 'simpleCoupon': {
      const simpleCoupon = coupon.type.simpleCoupon;

      return (
        <CouponLandingPageHeaderBaseMobile
          {...simpleCoupon}
          data-testid={dataTestId}
          isAppOnlyCoupon={isAppOnlyCoupon}
        />
      );
    }
    case 'minimumOrderValueCoupon': {
      const MOVCoupon = coupon.type.minimumOrderValueCoupon;

      if (MOVCoupon.tiers.length === 1) {
        const {
          headline,
          expiryDate,
          info,
          brandAndCategory,
          redeemableDevices,
        } = MOVCoupon;

        return (
          <CouponLandingPageHeaderBaseMobile
            couponPromotionId={MOVCoupon.couponPromotionId}
            headline={headline}
            expiryDate={expiryDate}
            info={info}
            brandAndCategory={brandAndCategory}
            redeemableDevices={redeemableDevices}
            data-testid={dataTestId}
            discountValue={MOVCoupon.tiers[0]?.discountValue}
            discountCondition={MOVCoupon.tiers[0]?.condition}
            isAppOnlyCoupon={isAppOnlyCoupon}
          />
        );
      }

      return (
        <CouponLandingPageHeaderBaseMobile
          {...MOVCoupon}
          data-testid={dataTestId}
          isAppOnlyCoupon={isAppOnlyCoupon}
        />
      );
    }

    case 'hotDealCoupon': {
      const hotDealCoupon = coupon.type.hotDealCoupon;

      return (
        <CouponLandingPageHeaderBaseMobile
          {...hotDealCoupon}
          data-testid={dataTestId}
          isAppOnlyCoupon={isAppOnlyCoupon}
        />
      );
    }

    case 'highTierCoupon': {
      const highTierCoupon = coupon.type.highTierCoupon;

      const relativeDiscountOrTiers =
        getRelativeDiscountOrTiers(highTierCoupon);
      return (
        <CouponLandingPageHeaderBaseMobile
          {...highTierCoupon}
          {...relativeDiscountOrTiers}
          isAppOnlyCoupon={isAppOnlyCoupon}
          data-testid={dataTestId}
        />
      );
    }

    default:
      return null;
  }
};
